<template>
  <div class="upload-page">
    <!-- Header -->
    <div class="viva-page-header">
      <div class="viva-header-content">
        <img src="/viva/viva_logo.svg" alt="Viva" class="viva-logo" />
        <h1 class="viva-page-title">UPLOAD YOUR ASSIGNMENT</h1>
      </div>
      <ProfileDropdown
        :student-name="studentName"
        :is-assignment="isAssignment"
      />
    </div>

    <!-- Rule Modal 1: Project -->
    <div v-if="currentRuleModal === 1" class="modal-backdrop" @click="handleRulesBackdropClick">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h2>Rules to Remember</h2>
          <div class="modal-progress">1 of 4</div>
        </div>
        <div class="modal-body">
          <div class="requirement-item">
            <i class="fa fa-file-text-o requirement-icon"></i>
            <div class="viva-requirement-content">
              <p>
                You must have a project or other assignment that you created and are prepared to talk about. 
              </p>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button data-cy="upload-confirmRule1" class="viva-btn viva-btn-orange viva-btn-block" @click="handleRuleUnderstand(1)">
            I understand
          </button>
        </div>
      </div>
    </div>

    <!-- Rule Modal 2: Practice Question -->
    <div v-if="currentRuleModal === 2" class="modal-backdrop" @click="handleRulesBackdropClick">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h2>Rules to Remember</h2>
          <div class="modal-progress">2 of 4</div>
        </div>
        <div class="modal-body">
          <div class="requirement-item">
            <i class="fa fa-video-camera requirement-icon"></i>
            <div class="viva-requirement-content">
              <p>
                After uploading your materials, you will have a chance to test your setup and 
                try a practice question that is unrelated to your project.
              </p>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button data-cy="upload-confirmRule2" class="viva-btn viva-btn-orange viva-btn-block" @click="handleRuleUnderstand(2)">
            I understand
          </button>
        </div>
      </div>
    </div>

    <!-- Rule Modal 3: Oral Exam Format -->
    <div v-if="currentRuleModal === 3" class="modal-backdrop" @click="handleRulesBackdropClick">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h2>Rules to Remember</h2>
          <div class="modal-progress">3 of 4</div>
        </div>
        <div class="modal-body">
          <div class="requirement-item">
            <i class="fa fa-microphone requirement-icon"></i>
            <div class="viva-requirement-content">
              <p>
                Once you begin the VIVA, you will be asked several questions about your work.
                You will {{ textQuestionsEnabled ? 'read' : 'hear' }} each question and have {{ Math.floor(answerTime * timeMultiplier) }} seconds to respond. There will be no breaks between questions.
                You can replay the question if you need, which will not count against your time.
                <span v-if="isAssignment">Once you are done, your VIVA will be automatically sent to your instructor.</span>
                <span v-else>Once you are done, you will be able to select in your account the institutions that will receive your VIVA recording.</span>
              </p>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button data-cy="upload-confirmRule3" class="viva-btn viva-btn-orange viva-btn-block" @click="handleRuleUnderstand(3)">
            I understand
          </button>
        </div>
      </div>
    </div>

    <!-- Rule Modal 4: Technical Requirements -->
    <div v-if="currentRuleModal === 4" class="modal-backdrop" @click="handleRulesBackdropClick">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h2>Rules to Remember</h2>
          <div class="modal-progress">4 of 4</div>
        </div>
        <div class="modal-body">
          <div class="requirement-item">
            <i class="fa fa-laptop requirement-icon"></i>
            <div class="viva-requirement-content">
              <p>
                You must be using a computer (not a phone or tablet) with a camera and microphone to complete your VIVA.
                We have optimized VIVA for Chrome browser.
              </p>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button data-cy="upload-confirmRule4" class="viva-btn viva-btn-orange viva-btn-block" @click="handleRuleUnderstand(4)">
            I understand
          </button>
        </div>
      </div>
    </div>

    <!-- Preparation Modal: After Upload Complete -->
    <div v-if="showPreparationModal" class="modal-backdrop" @click="handleRulesBackdropClick">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h2>Before You Begin</h2>
        </div>
        <div class="modal-body">
          <div class="requirement-item">
            <i class="fa fa-exclamation-triangle requirement-icon"></i>
            <div class="viva-requirement-content">
              <p>
                Please close all other programs on your computer, put away any other devices, and put away any notes.
                Trust us. If you have them out, you might be tempted to look in their direction, and then reviewers might think you are cheating.
              </p>
            </div>
          </div>
          <div class="form-group" style="margin-top: 25px;">
            <label for="preparationConfirm" style="display: block; margin-bottom: 10px; font-weight: 600; color: var(--viva-dark); text-align: center;">
              Type "I did" to confirm:
            </label>
            <input
              id="preparationConfirm"
              data-cy="upload-preparationInput"
              type="text"
              v-model="preparationConfirmText"
              placeholder="I did"
              class="form-control"
              @keyup.enter="handlePreparationConfirm"
              style="text-align: center;"
            />
          </div>
        </div>
        <div class="modal-footer">
          <button
            data-cy="upload-confirmPreparation"
            class="viva-btn viva-btn-orange viva-btn-block"
            @click="handlePreparationConfirm"
            :disabled="!isPreparationConfirmed"
          >
            Continue
          </button>
        </div>
      </div>
    </div>

    <!-- File Type Modal: Switch between text and muti-modal uploads -->
    <div v-if="currentRuleModal === 5" class="modal-backdrop" @click="handleRulesBackdropClick">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h2>Upload Type</h2>
        </div>
        <div class="modal-body">
          <div v-if="isMultiModal && onCnServer" class="requirement-item">
            <i class="fa fa-exclamation-triangle requirement-icon"></i>
            <div class="viva-requirement-content">
              <p class="text-warning">
                Sorry, multimodal uploads are not yet fully supported in China. Please try again with a text-based project, or contact support at
                <a href="mailto:viva@initialview.com">viva@initialview.com</a>
                to continue your VIVA.
              </p>
            </div>
          </div>
          <div v-else class="requirement-item">
            <i class="fa fa-file-image-o requirement-icon"></i>
            <div class="viva-requirement-content">
              <p>
                Your VIVA questions will be generated based on the materials you upload about your
                project. Please tell us what type of materials you will be uploading:
              </p>
              <label data-cy="upload-typePdf" class="type-label" :class="isMultiModal === false ? 'type-label-selected' : ''">
                <input class="type-radio" type="radio" name="fileType" :value="false" v-model="isMultiModal">
                <p>I have a paper in PDF format that is primarily text-based</p>
              </label>
              <label data-cy="upload-typeMultiModal" class="type-label" :class="isMultiModal === true ? 'type-label-selected' : ''">
                <input class="type-radio" type="radio" name="fileType" :value="true" v-model="isMultiModal">
                <p>I have a video, scan of handwritten notes, or document that is primarily image-based</p>
              </label>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button
            v-if="isMultiModal && onCnServer"
            data-cy="upload-returnToType"
            class="viva-btn viva-btn-orange viva-btn-block"
            @click="isMultiModal = null"
          >
            Back
          </button>
          <button
            v-else
            data-cy="upload-confirmType"
            class="viva-btn viva-btn-orange viva-btn-block"
            @click="handleRuleUnderstand(5)"
            :disabled="!isTypeConfirmed"
          >
            Continue
          </button>
        </div>
      </div>
    </div>

    <!-- Confirm Re-Upload Modal -->
    <div v-show="showResetUploadModal" class="modal-backdrop" @click="handleRulesBackdropClick">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h2>Are you sure?</h2>
        </div>
        <div class="modal-body">
          <div class="requirement-item">
            <i class="fa fa-exclamation-triangle requirement-icon"></i>
            <div class="viva-requirement-content">
              <p>
                Are you sure you want to re-upload your project?
                Any information you've entered about your project will be lost!
              </p>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button
            data-cy="upload-resetUpload-no"
            class="viva-btn viva-btn-orange viva-btn-block"
            @click="showResetUploadModal = false"
          >
            No
          </button>
          <button
            data-cy="upload-resetUpload-yes"
            class="viva-btn viva-btn-block"
            @click="resetUpload(true)"
          >
            Yes
          </button>
        </div>
      </div>
    </div>

    <!-- Main Upload Interface -->
    <div v-if="currentRuleModal === 0" class="viva-container">
      <div class="viva-section">
        
        <!-- Initial Upload State -->
        <div v-if="uploadState === 'initial'">
          <DragUpload
            data-cy="upload-fileUpload"
            v-on:fileurlchange="handleFileUpload"
            v-bind:file-info="fileInfo"
            :accept="acceptedUploadTypes"
          ></DragUpload>
          <p v-show="uploadInvalidType" class="upload-instructions text-warning">
            Sorry, that file format is not supported.
            <br />
            <span v-show="isMultiModal">
              Please export your project to a PDF, JPEG, PNG, MP4, WEBM, or MOV before uploading.
            </span>
            <span v-show="!isMultiModal">
              Please export your project to a PDF before uploading.
            </span>
          </p>
          <p class="upload-instructions">
            Upload your materials{{ isMultiModal === false ? ' as a PDF' : '' }}.
            Make sure it is based on a project you created and are ready to discuss.
            <span v-show="isMultiModal === true">
              <br />
              Accepted file types include PDF, JPG, PNG, MP4, WEBM, and MOV.
              <br />
              Videos must be less than 500MB and 10 minutes in length.
            </span>
          </p>
        </div>

        <!-- Uploading State -->
        <div v-if="uploadState === 'uploading'" class="processing-message">
          <div class="viva-spinner-section">
            <i class="fa fa-spinner fa-spin viva-spinner"></i>
            <p class="viva-spinner-text">
              Uploading your project{{ uploadFilename ? ':' : '...' }}
            </p>
            <p v-show="uploadFilename" class="viva-spinner-text project-filename">
              {{ uploadFilename }}
            </p>
          </div>
        </div>

        <!-- Project Title Input State -->
        <div v-if="uploadState === 'waiting_for_title'" class="paper-title-section">

          <div class="title-input-container">
            <h3>Enter your project title</h3>

            <p v-show="uploadFilename" class="viva-spinner-text project-filename">
              {{ uploadFilename }}
            </p>
            <p class="title-instructions">
              Please provide the title of your project. This will help reviewers understand your work.
            </p>
            <div class="form-group">
              <input
                data-cy="upload-projectTitle"
                type="text"
                v-model="paperTitle"
                placeholder="Enter your project title..."
                class="form-control paper-title-input"
                maxlength="500"
                @keyup.enter="submitPaperWithTitle"
              />
              <p class="text-muted">{{ paperTitle.length }}/500 characters</p>
            </div>

            <!-- Description of project for multi-modal files -->
            <div v-if="isMultiModal">
              <p class="title-instructions">
                Please provide a short description of your work
              </p>
              <div class="form-group">
                <textarea
                  data-cy="upload-projectDescription"
                  v-model="uploadDescription"
                  placeholder="Enter a short description of your project..."
                  class="form-control paper-title-input"
                  :maxlength="descriptionMaxCount"
                ></textarea>
                <p v-show="descriptionMaxCountClose" class="text-muted">{{ uploadDescription.length }}/{{ descriptionMaxCount }} characters</p>
              </div>
            </div>

            <!-- Name fields (only if student name is missing) -->
            <div v-if="!hasStudentName" class="name-fields">
              <p class="title-instructions">
                Please also provide your name
              </p>
              <div class="form-row">
                <div class="form-group">
                  <input
                    data-cy="upload-firstName"
                    type="text"
                    v-model="firstName"
                    placeholder="First Name"
                    class="form-control paper-title-input"
                  />
                </div>
                <div class="form-group">
                  <input
                    data-cy="upload-lastName"
                    type="text"
                    v-model="lastName"
                    placeholder="Last Name"
                    class="form-control paper-title-input"
                  />
                </div>
              </div>
            </div>

            <button
              data-cy="upload-infoSubmit"
              class="viva-btn viva-btn-orange viva-btn-block"
              @click="submitPaperWithTitle"
              :disabled="!isFormValid"
            >
              Continue
            </button>
          </div>
        </div>

        <!-- Back to Upload -->
         <div class="text-center" v-if="['uploading', 'waiting_for_title'].includes(uploadState)">
          <button
            data-cy="upload-resetUpload"
            class="viva-btn viva-btn-small"
            @click="resetUpload(uploadState === 'uploading' ? true : false)"
          >
            {{ uploadState === 'uploading' ? 'Cancel' : 'Upload Something Different' }}
          </button>
         </div>

        <!-- Processing State -->
        <div v-if="uploadState === 'processing'" class="processing-message">
          <div class="viva-spinner-section">
            <i class="fa fa-spinner fa-spin viva-spinner"></i>
            <p class="viva-spinner-text">Processing your project and generating questions...</p>
          </div>
        </div>

        <!-- Complete State -->
        <div v-if="uploadState === 'complete'" class="upload-complete">
          <div class="success-message">
            <i class="fa fa-check-circle"></i>
            <span>Materials uploaded successfully!</span>
          </div>

          <button data-cy="upload-submit" class="viva-btn viva-btn-orange viva-btn-block" @click="showPreparationModal = true">
            Start Viva!
          </button>
        </div>

        <!-- Error State -->
        <div v-if="uploadError" class="alert alert-danger">
          <p class="text-link">There has been an error with your upload. Please refresh the page and try again. If the problem persists, contact us at <a class="link-accent" href="mailto:viva@initialview.com">viva@initialview.com</a></p>
          <!-- {{ uploadError }} -->
        </div>
      </div>
      <!-- Back Button -->
      <div class="back-section">
        <router-link to="/" class="back-link">
          <i class="fa fa-arrow-left"></i> Back
        </router-link>
      </div>
    </div>
    
  </div>
</template>

<script>
import DragUpload from '../components/DragUpload.vue'
import ProfileDropdown from '../components/ProfileDropdown.vue'
import { setEnv, ivChunkUploadMixin } from 'iv-base-components'
import env from '../utils/env.js'
import apiClient from '../utils/apiClient.js'
import { retryUploadPaper } from '../utils/retryWithBackoff.js'
import { getStatusData } from '../utils/statusCache.js'

export default {
  name: 'Upload',
  mixins: [ivChunkUploadMixin],
  components: {
    DragUpload,
    ProfileDropdown
  },
  data() {
    return {
      currentRuleModal: 1, // 1, 2, 3, 4, 5 for the rule modals, 0 for none
      isMultiModal: null,
      showPreparationModal: false, // Modal shown after upload complete
      preparationConfirmText: '', // Text input for "I did" confirmation
      fileInfo: {
        fileUrl: null,
        fileIsReadOnly: false,
        fileIsImage: false
      },
      // Simplified state management
      uploadState: 'initial', // 'initial', 'uploading', 'waiting_for_title', 'processing', 'complete', 'error'
      uploadProgress: 0,
      uploadError: null,
      uploadInvalidType: false,
      uploadFilename: null,
      showResetUploadModal: false,
      paperTitle: '',
      uploadDescription: '',
      descriptionMaxCount: 1000,
      firstName: '',
      lastName: '',
      uploadedFileId: null,
      studentStatus: null,
      hasStudentName: true,  // Will be set from fetch_status
      isAssignment: false, // Will be set from fetch_status
      studentName: '',  // For profile dropdown
      textQuestionsEnabled: false, // Accommodation setting
      timeMultiplier: 1.0, // Accommodation setting
      answerTime: 60,
    }
  },
  computed: {
    isFormValid() {
      const hasPaperTitle = this.paperTitle.trim().length > 0;
      const hasName = this.hasStudentName || (this.firstName.trim() && this.lastName.trim());
      if (this.isMultiModal) {
        const hasDescription = this.uploadDescription.trim().length > 0;
        return hasPaperTitle && hasName && hasDescription;
      } else {
        return hasPaperTitle && hasName;
      }
    },

    isPreparationConfirmed() {
      return this.preparationConfirmText.trim().toLowerCase() === 'i did';
    },

    isTypeConfirmed() {
      if (this.onCnServer) {
        return this.isMultiModal === false;
      } else {
        return this.isMultiModal === true || this.isMultiModal === false;
      }
    },

    descriptionMaxCountClose() {
      return this.uploadDescription.length >= Math.round(this.descriptionMaxCount * 0.9);
    },

    acceptedUploadTypes() {
      let nonMultiModalTypes = ["application/pdf"];
      let multiModalTypes = [
        "image/jpeg",
        "image/png",
        "video/mp4",
        "video/webm",
        "video/quicktime",
      ];
      return this.isMultiModal ? nonMultiModalTypes.concat(multiModalTypes).join(",") : nonMultiModalTypes.join();
    },

    onCnServer() {
      return env.serverAddr.includes(".cn");
    },

  },
  mounted() {
    // Configure iv-base-components environment
    setEnv(env);

    // Check if user already has an uploaded project and get student name
    const statusData = getStatusData();
    if (statusData) {
      if (statusData.student_status) {
        this.studentStatus = statusData.student_status;

        if (['ready_to_record', 'ready_to_review', 'ready_to_pay', 'completed'].includes(statusData.student_status)) {
          this.uploadState = 'complete';
        }
      }

      // Check if student name exists (for coursework students)
      if (statusData.student_name !== undefined) {
        this.studentName = statusData.student_name;
        this.hasStudentName = statusData.student_name.trim().length > 1; // There's always a comma
      }
      if (statusData.is_assignment !== undefined) {
        this.isAssignment = statusData.is_assignment;
      }

      // Capture accommodation settings
      this.textQuestionsEnabled = statusData.text_questions_enabled || false;
      this.timeMultiplier = statusData.time_multiplier || 1.0;
      this.answerTime = statusData.answer_time || 60;
    }
  },
  methods: {
    handleRulesBackdropClick() {
      // Don't allow closing by clicking backdrop - user must click "I understand"
    },
    
    handleRuleUnderstand(ruleNumber) {
      if (ruleNumber < 5) {
        // Move to next rule modal
        this.currentRuleModal = ruleNumber + 1;
      } else {
        // All rules completed
        this.currentRuleModal = 0;
      }
    },

    resetUpload(confirmed) {
      if (!confirmed) {
        this.showResetUploadModal = true;
      } else {
        if (this.currentUpload) {
          this.currentUpload.abort();
          console.log('User canceled upload');
          this.currentUpload = null;
        }
        this.showResetUploadModal = false;
        this.uploadState = 'initial';
        this.uploadError = null;
        this.uploadInvalidType = false;
        this.uploadProgress = 0;
        this.uploadedFileId = null;
        this.uploadFilename = null;
        this.paperTitle = '';
        this.uploadDescription = '';
      }
    },
    
    handleFileUpload(event) {
      let that = this;

      // Prevent duplicate uploads
      if (that.uploadState === 'uploading') {
        console.log('Upload already in progress, ignoring duplicate event');
        return;
      }

      console.log('File upload event:', event);

      if (event.error) {
        that.uploadInvalidType = true;
        that.uploadFilename = null;
        return;
      }

      if (event.fileData && event.file) {
        that.uploadState = 'uploading';
        that.uploadError = null;
        that.uploadInvalidType = false;
        that.uploadProgress = 0;
        that.uploadFilename = event.file.name;

        console.log('Starting file upload:', event.file.name);

        // Check filename length and truncate if over 90 characters
        let fileToUpload = event.file;
        if (event.file.name.length > 90) {
          const extension = event.file.name.split('.').pop();
          const nameWithoutExtension = event.file.name.substring(0, event.file.name.lastIndexOf('.'));
          const maxNameLength = 90 - extension.length - 1; // -1 for the dot
          const truncatedName = nameWithoutExtension.substring(0, maxNameLength) + '.' + extension;

          // Create a new file object with the truncated name
          fileToUpload = new File([event.file], truncatedName, { type: event.file.type });

          console.log(`Filename truncated from "${event.file.name}" to "${truncatedName}"`);
        }

        // Use chunkUpload from ivChunkUploadMixin
        that.currentUpload = that.chunkUpload(
          "/tus_files/",
          fileToUpload,
          that,
          "uploadProgress",
          that.onUploadComplete,
          that.onUploadError
        );
      } else if (event.fileData === null) {
        // File was removed
        that.uploadState = 'initial';
        that.uploadError = null;
        that.uploadInvalidType = false;
        that.uploadProgress = 0;
        that.uploadedFileId = null;
        that.uploadFilename = null;
      }
    },
    
    async onUploadComplete(resource_id) {
      let that = this;
      console.log('Upload completed, resource_id:', resource_id);

      // Ignore completion if the user has already cancelled the upload
      if (that.uploadState !== 'uploading') {
        return;
      }

      // Store the uploaded file ID and transition to title input
      that.uploadedFileId = resource_id;
      that.uploadState = 'waiting_for_title';
    },
    
    onUploadError(error) {
      let that = this;
      console.error('Upload failed:', error);

      // Ignore error if the user has already cancelled the upload
      if (that.uploadState !== 'uploading') {
        return;
      }

      that.uploadError = 'Upload failed: ' + error.message.slice(0,500);
      that.uploadState = 'error';
      that.uploadProgress = 0;
    },

    async submitPaperWithTitle() {
      if (!this.isFormValid) {
        this.uploadError = 'Please complete all required fields';
        return;
      }

      // Account for the user cancelling the upload
      if (!this.uploadedFileId || this.uploadState === 'initial') {
        return;
      }

      this.uploadState = 'processing';
      this.uploadError = null;

      try {
        const vparam = sessionStorage.getItem('vparam');

        // Use retry mechanism instead of fixed delay
        const uploadPaperOperation = async () => {
          const uploadData = {
            uploaded_paper_uid: this.uploadedFileId,
            paper_title: this.paperTitle.trim(),
            use_multimodal: this.isMultiModal
          };

          // Add description if multi-modal
          if (this.isMultiModal) {
            uploadData.project_description = this.uploadDescription.trim()
          }

          // Add vparam and names if coursework student
          if (vparam) {
            uploadData.vparam = vparam;
            // Only send names if they don't exist yet
            if (!this.hasStudentName) {
              uploadData.first_name = this.firstName.trim();
              uploadData.last_name = this.lastName.trim();
            }
          }

          const response = await apiClient.post('/api/viva/upload_paper/', uploadData);
          if (response.data.status !== 200 && response.data.status !== 201) {
            const error = new Error(response.data.error || 'Upload project endpoint failed');
            error.responseStatus = response.data.status;
            throw error;
          }
          return response;
        };

        const response = await retryUploadPaper(uploadPaperOperation, this.uploadedFileId);

        this.studentStatus = response.data.student_status;
        this.uploadState = 'complete';
        console.log('VivaExam created successfully:', response.data);

        // Store the exam_id as vparam for subsequent pages (admissions flow)
        // Coursework flow already has vparam from the assignment link
        if (response.data.exam_id && !sessionStorage.getItem('vparam')) {
          sessionStorage.setItem('vparam', response.data.exam_id);
        }
      } catch (error) {
        console.error('Error creating VivaExam:', error);
        this.uploadError = 'Failed to process uploaded file: ' + (error.response?.data?.error || error.message);
        this.uploadState = 'error';
      }
    },

    handlePreparationConfirm() {
      if (!this.isPreparationConfirmed) {
        return;
      }
      // Navigate directly without closing modal to avoid flash of content
      this.proceedToPayment();
    },

    proceedToPayment() {
      if (this.uploadState !== 'complete') {
        this.uploadError = 'Please complete the upload process before proceeding'
        return
      }

      // Check if coursework (status will be ready_to_record) or admissions (ready_to_pay)
      if (this.studentStatus === 'ready_to_record') {
        // Coursework - skip payment, go straight to practice
        this.$router.push('/practice')
      } else {
        // Admissions - go to payment
        this.$router.push('/payment')
      }
    }
  }
}
</script>

<style scoped>
@import '../styles/viva-classes.css';

.upload-page {
  min-height: 100vh;
  background-color: var(--viva-cream);
  margin: 0;
  padding: 0;
  position: relative;
  top: 0;
}

/* Page-specific styles only */

/* Container */
.container {
  margin: 0 auto;
  padding: 0 20px;
}

.upload-instructions {
  text-align: center;
  margin: 20px 0 20px 0;
  font-size: 16px;
  line-height: 1.5;
}

/* Upload Complete Section */
.upload-complete {
  margin-top: 20px;
  text-align: center;
}

.success-message {
  background: var(--viva-cream);
  border: 2px solid var(--viva-green);
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 20px;
  color: var(--viva-dark);
  font-size: 18px;
  font-weight: 500;
}

.success-message i {
  color: var(--viva-green);
  font-size: 24px;
  margin-right: 10px;
}

/* Alert Styles */
.alert {
  padding: 15px;
  border-radius: 8px;
  margin: 15px 0;
  font-weight: 500;
}

.alert-danger {
  background-color: #f8d7da;
  border: 1px solid #f5c6cb;
  color: #721c24;
}

/* Modal Styles */
.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background: var(--viva-white);
  border-radius: 12px;
  max-width: 600px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  border: 3px solid var(--viva-green);
}

.payment-modal {
  max-width: 500px;
}

.modal-header {
  background: var(--viva-green);
  color: var(--viva-white);
  padding: 20px;
  border-radius: 9px 9px 0 0;
  position: relative;
}

.modal-header h2 {
  margin: 0;
  font-size: 24px;
  font-weight: 600;
  text-align: center;
}

.modal-progress {
  position: absolute;
  top: 15px;
  right: 20px;
  font-size: 14px;
  font-weight: 500;
  background: rgba(255, 255, 255, 0.2);
  padding: 5px 10px;
  border-radius: 15px;
}

.modal-body {
  padding: 25px;
}

.modal-footer {
  padding: 20px;
  border-top: 1px solid #eee;
}

/* Rules Modal Styles */
.requirement-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  margin-bottom: 0;
  padding: 30px;
  background: linear-gradient(135deg, var(--viva-cream) 0%, #f8f8f8 100%);
  border-radius: 8px;
  border: 2px solid var(--viva-green);
}

.requirement-icon {
  color: var(--viva-orange);
  font-size: 48px;
  margin-bottom: 20px;
  flex-shrink: 0;
}

.requirement-item p {
  margin: 0;
  line-height: 1.7;
  font-size: 18px;
  max-width: 500px;
}

/* Type Modal Styles */

.type-label {
  margin: 10px 0px;
  border: 2px solid var(--viva-orange);
  border-radius: 8px;
  padding: 16px;
  display: flex;
  gap: 16px;
  align-items: center;
  cursor: pointer;
}

.type-label p {
  flex-grow: 1;
}

.type-label:hover,
.type-label:active,
.type-label-selected {
  background-color: var(--viva-orange);
}

.type-label:hover p,
.type-label:active p,
.type-label-selected p {
  color: var(--viva-white);
}

.type-radio {
  margin: 0;
  width: 20px;
  height: 20px;
  accent-color: var(--viva-white);
  flex-shrink: 0;
}

/* Payment Modal Styles */
.payment-details {
  text-align: center;
}

.price-display {
  margin-bottom: 25px;
  padding: 20px;
  background: var(--viva-cream);
  border-radius: 8px;
  border: 2px solid var(--viva-green);
}

.price-display h3 {
  margin: 0 0 10px 0;
  color: var(--viva-dark);
  font-size: 24px;
  font-weight: 600;
}

.price-display p {
  margin: 0;
  color: var(--viva-grey);
  font-size: 14px;
}

/* Payment Method Tabs */
.payment-method-tabs {
  display: flex;
  margin-bottom: 20px;
  border-radius: 8px;
  overflow: hidden;
  border: 2px solid var(--viva-orange);
}

.payment-tab {
  flex: 1;
  padding: 12px 20px;
  border: none;
  background: var(--viva-cream);
  color: var(--viva-dark);
  cursor: pointer;
  transition: all 0.2s ease;
  font-weight: 500;
}

.payment-tab:hover {
  background: var(--viva-orange);
  color: var(--viva-white);
}

.payment-tab.active {
  background: var(--viva-orange);
  color: var(--viva-white);
}

.payment-tab i {
  margin-right: 8px;
}

/* Mock Payment Forms */
.mock-payment-form {
  text-align: left;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #dee2e6;
}

.form-group {
  margin-bottom: 15px;
}

.form-row {
  display: flex;
  gap: 15px;
  justify-content: center;
}

.col-6 {
  flex: 1;
}

.form-group label {
  display: block;
  margin-bottom: 5px;
  font-weight: 500;
  color: var(--viva-dark);
}

.form-control {
  width: 100%;
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 14px;
  background: #fff;
}

.form-control:disabled {
  background: #f8f9fa;
  color: var(--viva-grey);
}

.paypal-placeholder {
  padding: 20px;
  text-align: center;
}

.mock-paypal-button {
  background: #0070ba;
  color: white;
  padding: 12px 24px;
  border-radius: 4px;
  display: inline-block;
  margin-top: 10px;
  font-weight: 500;
}

/* Responsive Design */
@media (max-width: 768px) {
  .header-content {
    flex-direction: column;
    gap: 10px;
  }
  
  .page-title {
    font-size: 24px;
  }
  
  .viva-logo {
    height: 40px;
    padding: 6px 3px
  }
  
  .modal-content {
    width: 95%;
    margin: 10px;
  }
  
  .requirement-item {
    flex-direction: column;
    text-align: center;
  }
  
  .requirement-icon {
    margin-right: 0;
    margin-bottom: 10px;
  }
  
  .payment-method-tabs {
    flex-direction: column;
  }
  
  .form-row {
    flex-direction: column;
    gap: 10px;
  }
}


@keyframes progress-bar-stripes {
  from {
    background-position: 40px 0;
  }
  to {
    background-position: 0 0;
  }
}

/* Replace Project Section */
.replace-paper-section {
  margin-top: 15px;
  text-align: center;
}

/* Locked File Message */
.file-locked-message {
  text-align: center;
  margin: 20px 0;
}

.locked-file-display {
  background: #f8f9fa;
  border: 2px dashed #dee2e6;
  border-radius: 8px;
  padding: 40px 20px;
  color: #6c757d;
}

.locked-file-display i {
  font-size: 48px;
  color: #adb5bd;
  margin-bottom: 15px;
  display: block;
}

.locked-file-display p {
  margin: 5px 0;
}

.locked-file-display strong {
  color: #495057;
}

/* Button Overrides for this page */
.viva-btn-block {
  margin-top: 15px;
}

/* Upload filename */
.project-filename {
  color: var(--viva-green) !important;
  overflow-wrap: break-word;
}

/* Project Title Input Section */
.paper-title-section {
  text-align: center;
  margin: 20px 0;
}

.title-input-container {
  margin-top: 25px;
  max-width: 500px;
  margin-left: auto;
  margin-right: auto;
}

/* Name fields */
.name-fields {
  margin-bottom: 25px;
  width: 100%;
}


.title-input-container h3 {
  color: var(--viva-dark);
  font-size: 24px;
  font-weight: 600;
  margin-bottom: 15px;
}

.title-instructions {
  color: var(--viva-grey);
  font-size: 16px;
  line-height: 1.4;
  margin-bottom: 20px;
}

.paper-title-input {
  width: 100%;
  max-width: 100%;
  min-width: 100%;
  max-height: 200px;
  padding: 12px 16px;
  border: 2px solid #dee2e6;
  border-radius: 8px;
  font-size: 16px;
  line-height: 1.5;
  background: #fff;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}

.paper-title-input:focus {
  border-color: var(--viva-green);
  box-shadow: 0 0 0 0.2rem rgba(40, 167, 69, 0.25);
  outline: 0;
}

.paper-title-input::placeholder {
  color: #6c757d;
}

.form-group {
  margin-bottom: 20px;
  text-align: left;
}

.form-group .text-muted {
  font-size: 12px;
  color: var(--viva-grey);
  margin-top: 5px;
  margin-bottom: 0;
  text-align: right;
}

/* Processing State */
.processing-message {
  text-align: center;
  margin: 40px 0;
}


.processing-message .viva-spinner-text {
  color: var(--viva-grey);
  font-size: 16px;
  font-weight: 500;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .title-input-container {
    margin: 20px 15px;
    max-width: none;
  }

  .paper-title-input {
    font-size: 16px; /* Prevents zoom on iOS */
  }
}
</style>
